import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp } from 'lucide-react';
import AnalyticsChartFullscreenButton from './AnalyticsChartFullscreenButton';
// Re-exported for back-compat — moved to a shared hooks module (C4) so
// non-analytics code doesn't have to reach into the analytics folder for a
// generic breakpoint hook. Prefer importing from '../../hooks/useMediaQuery'
// directly in new code.
export { useIsBelowLg } from '../../hooks/useMediaQuery';

/**
 * Shared body padding/gap for preset chart cards. Mobile trims padding to
 * 8px (`px-2`) and tightens the gap; `lg:` restores the original desktop
 * values (`px-4`, and `pt-4 pb-4 gap-4` / `gap-3` depending on mode).
 */
export function presetChartBodyClass(forceFiltersOpen, isFullscreen = false) {
  if (forceFiltersOpen && isFullscreen) {
    return 'px-2 pt-3 pb-3 gap-1.5 lg:px-4 lg:pt-4 lg:pb-4 lg:gap-2';
  }
  return forceFiltersOpen
    ? 'px-2 pt-3 pb-0 gap-1.5 lg:px-4 lg:pt-4 lg:pb-0 lg:gap-2'
    : 'px-2 gap-2 lg:px-4 lg:gap-3';
}

/**
 * Vertical rhythm around the filter dropdown row, shared by every preset
 * chart: 8px (lg) / 6px (below lg) above and below the row. Literal class
 * strings so Tailwind generates them.
 */
export const PRESET_FILTER_GAP = 'gap-1.5 lg:gap-2';
export const PRESET_FILTER_ROW_Y = 'py-1.5 lg:py-2';
export const PRESET_FILTER_OPEN_PT = 'max-lg:pt-1.5';
export const PRESET_PLOT_CAPTION_PAD = 'px-4 pt-2 pb-1.5';

/**
 * Shared card chrome for the Analytics preset views.
 *
 * Every preset section renders the same header: a sentence-case title on its
 * own row, then a single scrollable line of compact filter chips with the
 * Fullscreen action pinned to its right. These two components own that layout
 * so the eight chart widgets don't each carry their own copy of it.
 *
 * Both fall back to the widget-dashboard presentation when `forceFiltersOpen`
 * is false, so the Custom view is unaffected.
 */

/**
 * Card title. Preset: 16px semibold, no rule. Widget dashboard: the legacy
 * uppercase heading over a bottom border.
 */
export const PresetChartTitle = ({
  forceFiltersOpen,
  title,
  theme,
  fontSize,
  /** Rendered next to the heading (e.g. ChartHelpButton). */
  titleAfter = null,
  /** Rendered at the far right of the row (e.g. the fullscreen Exit control). */
  right = null,
  className = '',
}) => (
  <div
    className={
      (forceFiltersOpen ? 'flex-shrink-0' : 'border-b py-2 flex-shrink-0') +
      (className ? ` ${className}` : '')
    }
    style={forceFiltersOpen ? undefined : { borderColor: theme?.borderColor }}
  >
    <div className="flex items-center justify-between gap-3">
      <div className="flex min-w-0 items-center gap-1">
        {forceFiltersOpen ? (
          <h3 className="max-lg:truncate text-[16px] font-semibold text-[#282A29] dark:text-white">{title}</h3>
        ) : (
          <h3
            className="max-lg:truncate uppercase font-bold tracking-wide"
            style={{ fontSize: (fontSize ?? 14) + 4, color: theme?.titleColor }}
          >
            {title}
          </h3>
        )}
        {titleAfter}
      </div>
      {right}
    </div>
  </div>
);

/**
 * Collapsible chart legend (the per-series color-key list rendered under a
 * chart in place of Plotly's built-in legend). Collapsed by default — the
 * series list can run to dozens of rows once many filters are selected, so
 * it shouldn't cost vertical space until asked for. The caller keeps its own
 * gate (e.g. `presetDescription && hideLegend && ...`) and passes the legend
 * items as `children`; this component only owns the toggle chrome.
 */
export const PresetChartLegend = ({ theme, children, fullscreen = false }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="flex-shrink-0">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-label={expanded ? t('filters.hideLegend') : t('filters.showLegend')}
        className={`w-full flex items-center justify-center ${fullscreen ? 'py-1' : 'pt-1'}`}
      >
        {expanded ? (
          <ChevronUp size={16} style={{ color: theme?.secondaryText }} />
        ) : (
          <ChevronDown size={16} style={{ color: theme?.secondaryText }} />
        )}
      </button>
      {expanded ? (
        <div className={`px-4 pt-1 ${fullscreen ? 'pb-3' : ''} flex flex-wrap items-center justify-start gap-x-4 gap-y-2`}>
          <div className="flex flex-wrap items-center justify-start gap-x-5 gap-y-2 min-w-0 flex-1" role="list">
            {children}
          </div>
        </div>
      ) : null}
    </div>
  );
};

/**
 * Filter strip. Preset: one horizontally scrollable line of compact chips plus
 * the Fullscreen button. Widget dashboard: the original wrapping grid, passed
 * in as `gridClassName` so each chart keeps its own column count.
 *
 * Pass `compact={forceFiltersOpen}` to the FilterDropdown children — this only
 * owns the row, not the chips.
 */
export const PresetFilterStrip = ({
  forceFiltersOpen,
  presetSingleChartFullscreen = false,
  analyticsFullscreen = null,
  gridClassName = 'pt-2 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2',
  /**
   * Pass true when the caller's title row already renders
   * ChartFullscreenTitleControl (compact enter control, <lg). That control
   * makes this row's own enter button redundant below lg, so it hides there.
   * Defaults false so callers without a title-adjacent control (e.g. the
   * Global Produce Map) keep their only enter-fullscreen affordance visible
   * at every breakpoint.
   */
  titleHasFullscreenControl = false,
  /**
   * Extra classes for the chips container only, leaving the row (and so the
   * Fullscreen button) in place. Used by callers that put the chips behind
   * their own "Filters" toggle on small screens but have no other
   * enter-fullscreen affordance to fall back on (e.g. the Global Produce Map):
   * they pass `max-lg:hidden` while collapsed and `max-lg:pt-1.5` while open.
   */
  chipsClassName = '',
  children,
}) => {
  if (!forceFiltersOpen) {
    return <div className={gridClassName}>{children}</div>;
  }

  const showFullscreen =
    !presetSingleChartFullscreen && analyticsFullscreen?.onEnter && analyticsFullscreen?.onExit;

  return (
    <div data-preset-filter-strip className="flex items-end gap-4">
      {/* Below lg the chips lay out on an auto-fit grid: as many equal columns
          as fit at >=120px each, so every row spans the full width and the
          chips stay aligned in columns instead of leaving ragged dead space.
          The chips themselves fill their column (see FilterDropdown's compact
          branch); values stay on one 27px line and truncate with an ellipsis,
          so every row keeps the same height. From lg up it reverts to the
          single scrollable no-wrap line of content-sized chips. */}
      <div
        className={`grid min-w-0 flex-1 grid-cols-[repeat(auto-fit,minmax(120px,1fr))] items-end gap-2 lg:flex lg:flex-nowrap lg:justify-start lg:overflow-x-auto lg:scrollbar-hide${
          chipsClassName ? ` ${chipsClassName}` : ''
        }`}
      >
        {children}
      </div>
      {showFullscreen ? (
        <div className={`shrink-0 ${titleHasFullscreenControl ? 'hidden lg:block' : ''}`}>
          <AnalyticsChartFullscreenButton
            isActive={analyticsFullscreen.isActive}
            onEnter={analyticsFullscreen.onEnter}
            onExit={analyticsFullscreen.onExit}
          />
        </div>
      ) : null}
    </div>
  );
};
